import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { formatCurrency } from '../../utils/calculations';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import { useToast } from '../ToastProvider';

const MenuItemCard = ({ item, onAddToOrder, customerPhone, isFavorite = false, onFavoriteChange, isGuest = false }) => {
    const [isAddingToFavorites, setIsAddingToFavorites] = useState(false);
    const [imageErrored, setImageErrored] = useState(false);
    const addToast = useToast();

    const fallbackSig = (() => {
        const s = String(item?._id || item?.itemName || 'fallback');
        let hash = 0;
        for (let i = 0; i < s.length; i += 1) {
            hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
        }
        return 1000 + (hash % 8000);
    })();

    // Use a very reliable placeholder host (avoids redirect blocks)
    const fallbackImage = `https://picsum.photos/seed/menu-${fallbackSig}/1200/900`;

    const handleAddToOrder = () => {
        onAddToOrder(item);
        addToast(`${item.itemName} added to order!`, 'success');
    };

    const handleToggleFavorite = async () => {
        if (!customerPhone) {
            addToast('Please enter your phone number to add favorites', 'warning');
            return;
        }

        setIsAddingToFavorites(true);
        try {
            if (isFavorite) {
                await axios.delete(API_ENDPOINTS.CUSTOMER_REMOVE_FAVORITE(customerPhone), {
                    data: { itemId: item._id }
                });
                addToast('Removed from favorites', 'success');
            } else {
                await axios.post(API_ENDPOINTS.CUSTOMER_ADD_FAVORITE(customerPhone), {
                    itemId: item._id
                });
                addToast('Added to favorites!', 'success');
            }
            if (onFavoriteChange) {
                onFavoriteChange(item._id, !isFavorite);
            }
        } catch (error) {
            addToast('Failed to update favorites', 'error');
            console.error('Favorite toggle error:', error);
        } finally {
            setIsAddingToFavorites(false);
        }
    };

    return (
        <motion.div 
            className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-md sm:shadow-lg hover:shadow-lg hover:border-amber-200 transition-all duration-300 overflow-hidden border border-stone-200 w-full min-w-0 h-full flex flex-col"
            whileHover={{ y: -1, scale: 1.005 }}
            transition={{ duration: 0.2 }}
        >
            {(item.itemImage || imageErrored) && (
                <div className="relative overflow-hidden flex-shrink-0">
                    <img 
                        src={imageErrored ? fallbackImage : item.itemImage} 
                        alt={item.itemName} 
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        className="w-full h-28 sm:h-36 md:h-40 lg:h-48 object-cover transition-transform duration-300 hover:scale-105"
                        onError={() => setImageErrored(true)}
                    />
                    <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                        {item.itemType === 'veg' ? (
                            <div className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 border border-green-500 bg-white flex items-center justify-center rounded shadow-sm">
                                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 lg:w-3 lg:h-3 bg-green-500 rounded-full"></div>
                            </div>
                        ) : (
                            <div className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 border border-red-500 bg-white flex items-center justify-center rounded shadow-sm">
                                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 lg:w-3 lg:h-3 bg-red-500 rounded-full"></div>
                            </div>
                        )}
                        {item.quickServe && (
                            <span className="text-xs bg-orange-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full font-semibold shadow-sm">
                                <span className="hidden sm:inline">⚡ Quick</span>
                                <span className="sm:hidden">⚡</span>
                            </span>
                        )}
                        {item.isDummy && (
                            <span className="text-xs bg-stone-900/80 text-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full font-semibold shadow-sm">
                                Sample
                            </span>
                        )}
                    </div>
                    {customerPhone && !isGuest && (
                        <motion.button
                            onClick={handleToggleFavorite}
                            disabled={isAddingToFavorites}
                            className={`absolute top-2 right-2 w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center transition-all duration-300 shadow-sm ${
                                isFavorite 
                                    ? 'bg-red-500 text-white shadow-lg' 
                                    : 'bg-white/90 text-gray-600 hover:bg-white hover:text-red-500'
                            }`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {isAddingToFavorites ? (
                                <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-current"></div>
                            ) : (
                                <span className="text-sm sm:text-base lg:text-lg">{isFavorite ? '❤️' : '🤍'}</span>
                            )}
                        </motion.button>
                    )}
                </div>
            )}
            
            <div className="p-2.5 sm:p-3 md:p-4 flex-1 flex flex-col min-w-0">
                <div className="flex justify-between items-start gap-2 mb-1.5 sm:mb-2 min-w-0">
                    <h3 className="text-sm sm:text-base font-bold text-stone-800 leading-tight flex-1 min-w-0 line-clamp-2 pr-1">{item.itemName}</h3>
                    <div className="text-sm sm:text-base font-bold text-amber-600 flex-shrink-0">
                        {formatCurrency(item.itemPrice)}
                    </div>
                </div>

                {item.itemDescription && (
                    <p className="text-stone-500 text-xs sm:text-sm leading-relaxed mb-2 sm:mb-3 line-clamp-2">
                        {item.itemDescription}
                    </p>
                )}

                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                    {item.itemCategory && (
                        <span className="text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full font-medium">
                            {item.itemCategory}
                        </span>
                    )}
                    {!item.isAvailable && (
                        <span className="text-xs bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full font-medium">
                            Out of Stock
                        </span>
                    )}
                </div>

                {!item.isDummy && (
                    <motion.button
                        onClick={handleAddToOrder}
                        disabled={!item.isAvailable}
                        className={`w-full mt-auto py-1.5 sm:py-2 rounded-lg font-semibold text-xs transition-all duration-300 inline-flex items-center justify-center gap-1.5 ${
                            item.isAvailable
                                ? 'bg-amber-500 hover:bg-amber-600 text-white shadow shadow-amber-500/20 hover:shadow-amber-500/25'
                                : 'bg-stone-200 text-stone-500 cursor-not-allowed'
                        }`}
                        whileHover={item.isAvailable ? { scale: 1.01 } : {}}
                        whileTap={item.isAvailable ? { scale: 0.99 } : {}}
                    >
                        {item.isAvailable ? (
                            <>
                                <span className="text-sm" aria-hidden="true">📋</span>
                                <span>Order</span>
                            </>
                        ) : (
                            <>
                                <span className="text-xs" aria-hidden="true">❌</span>
                                <span>Unavailable</span>
                            </>
                        )}
                    </motion.button>
                )}
            </div>
        </motion.div>
    );
};

export default MenuItemCard;